import { Platform, TextStyle } from 'react-native';

/**
 * Font configuration that works seamlessly across platforms:
 * - Web: Uses CSS @font-face defined in +html.tsx (loads before JS)
 * - Native: Uses expo-google-fonts loaded via FontContext
 * 
 * The key insight: on web, we define the font in CSS so it's available
 * immediately - no need to wait for JS to load fonts.
 */

// Web: Use 'Inter' (defined in +html.tsx @font-face) with size-adjusted fallback
// Native: Use expo-google-fonts naming convention
const fontFamilies = {
  regular: Platform.select({
    web: 'Inter, Inter-fallback, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    ios: 'Inter_400Regular',
    android: 'Inter_400Regular',
    default: 'Inter_400Regular',
  }),
  medium: Platform.select({
    web: 'Inter, Inter-fallback, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    ios: 'Inter_500Medium',
    android: 'Inter_500Medium',
    default: 'Inter_500Medium',
  }),
  semiBold: Platform.select({
    web: 'Inter, Inter-fallback, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    ios: 'Inter_600SemiBold',
    android: 'Inter_600SemiBold',
    default: 'Inter_600SemiBold',
  }),
} as const;

// Font weight mappings
const fontWeights = {
  regular: '400' as TextStyle['fontWeight'],
  medium: '500' as TextStyle['fontWeight'],
  semiBold: '600' as TextStyle['fontWeight'],
};

/**
 * Pre-configured font styles ready to use.
 * On web, these work immediately without waiting for fonts to load.
 * On native, FontContext must load fonts first.
 */
export const fonts = {
  // Display styles (for hero titles)
  displayLarge: {
    fontFamily: fontFamilies.regular,
    fontSize: 58,
    fontWeight: fontWeights.regular,
    lineHeight: 58 * 1.06,
    letterSpacing: -0.015 * 58,
  } as TextStyle,
  
  displayMedium: {
    fontFamily: fontFamilies.regular,
    fontSize: 48,
    fontWeight: fontWeights.regular,
    lineHeight: 48 * 1.1,
    letterSpacing: -0.34,
  } as TextStyle,

  // Heading styles
  h1: {
    fontFamily: fontFamilies.semiBold,
    fontSize: 32,
    fontWeight: fontWeights.semiBold,
    lineHeight: 32 * 1.2,
  } as TextStyle,

  h2: {
    fontFamily: fontFamilies.semiBold,
    fontSize: 24,
    fontWeight: fontWeights.semiBold,
    lineHeight: 24 * 1.2,
  } as TextStyle,

  h3: {
    fontFamily: fontFamilies.semiBold,
    fontSize: 20,
    fontWeight: fontWeights.semiBold,
    lineHeight: 20 * 1.2,
  } as TextStyle,

  // Body text
  bodyLarge: {
    fontFamily: fontFamilies.regular,
    fontSize: 18,
    fontWeight: fontWeights.regular,
    lineHeight: 18 * 1.5,
  } as TextStyle,

  bodyMedium: {
    fontFamily: fontFamilies.regular,
    fontSize: 16,
    fontWeight: fontWeights.regular,
    lineHeight: 16 * 1.5,
  } as TextStyle,

  bodySmall: {
    fontFamily: fontFamilies.regular,
    fontSize: 14,
    fontWeight: fontWeights.regular,
    lineHeight: 14 * 1.5,
  } as TextStyle,

  // Special styles
  button: {
    fontFamily: fontFamilies.medium,
    fontSize: 16,
    fontWeight: fontWeights.medium,
    letterSpacing: 0.08,
  } as TextStyle,

  label: {
    fontFamily: fontFamilies.medium,
    fontSize: 12,
    fontWeight: fontWeights.medium,
    letterSpacing: 0.24,
  } as TextStyle,
};

/**
 * Direct font family exports for components that need 
 * to apply fonts conditionally or with custom styles.
 */
export const fontFamily = {
  regular: fontFamilies.regular,
  medium: fontFamilies.medium,
  semiBold: fontFamilies.semiBold,
};

/**
 * Helper to get the right font family for a given weight.
 * Useful when you need dynamic font weights.
 */
export function getFontFamily(weight: 'regular' | 'medium' | 'semiBold' = 'regular'): string {
  return fontFamilies[weight] as string;
}

/**
 * Ready-to-use font styles that work on both web and native.
 * Use these instead of hardcoded font family names.
 * 
 * Example:
 *   Before: fontsLoaded && interSemiBold
 *   After:  fontsLoaded && interSemiBold
 */
export const interRegular: TextStyle = {
  fontFamily: fontFamilies.regular,
  fontWeight: fontWeights.regular,
};

export const interMedium: TextStyle = {
  fontFamily: fontFamilies.medium,
  fontWeight: fontWeights.medium,
};

export const interSemiBold: TextStyle = {
  fontFamily: fontFamilies.semiBold,
  fontWeight: fontWeights.semiBold,
};
